/**
 * StageBoard — one board per pipeline stage. A header with the stage label,
 * the current count and a state chip (when the state has one); an optional
 * "promote a generation scenario" step for Stage 2; then the surface's own
 * one-line rows, laid out on the shared `StageRow` grid: glyph · name + one
 * line of meta · state · one action. Surfaces keep their own row internals
 * (and test ids); the board keeps the two pages looking and reading alike.
 */
import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../../ui/button';
import { StatusPill } from '../../ui/status-pill';
import { type StageBoardModel, type StageScenarioStep } from '../../../lib/pathToGo/stageBoards';
import type { PathAction } from '../../../lib/pathToGo/reportPlan';
import { stageStateChip } from './StageStrip';

export type StageGlyphKind = 'current' | 'ready' | 'running' | 'warn' | 'danger' | 'later' | 'priority';

/** The row's left-hand mark: a priority number, a tick, or a state ring. */
export const StageGlyph: React.FC<{
  kind: StageGlyphKind;
  priority?: number;
  className?: string;
  'data-testid'?: string;
}> = ({ kind, priority, className, 'data-testid': testId }) => {
  const base = 'mt-0.5 inline-grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border-[1.5px] text-[10px] font-bold tabular-nums';
  const byKind: Record<StageGlyphKind, string> = {
    priority: 'border-primary bg-primary text-primary-foreground',
    current: 'border-success bg-success text-primary-foreground',
    ready: 'border-primary text-primary',
    running: 'border-dashed border-primary text-primary',
    warn: 'border-warning text-warning',
    danger: 'border-danger bg-danger/10 text-danger',
    later: 'border-dashed border-border text-text-muted',
  };
  return (
    <span className={cn(base, byKind[kind], className)} data-testid={testId} aria-hidden>
      {kind === 'priority' ? priority : kind === 'current' ? '✓' : kind === 'warn' ? '!' : kind === 'danger' ? '×' : kind === 'running' ? '…' : ''}
    </span>
  );
};

/** The shared row grid. Children go in the four slots. */
export const StageRow: React.FC<{
  glyph: React.ReactNode;
  main: React.ReactNode;
  state?: React.ReactNode;
  action?: React.ReactNode;
  next?: boolean;
  dim?: boolean;
  id?: string;
  className?: string;
  'data-testid'?: string;
  [dataAttr: `data-${string}`]: string | undefined;
}> = ({ glyph, main, state, action, next = false, dim = false, id, className, ...rest }) => (
  <li
    id={id}
    className={cn(
      'grid min-h-[46px] grid-cols-[22px_minmax(0,1fr)] items-center gap-x-2.5 gap-y-1.5 border-b border-border-subtle px-3.5 py-2 last:border-b-0 @[720px]:grid-cols-[22px_minmax(12rem,1fr)_minmax(0,15rem)_auto] @[720px]:gap-x-3',
      next && 'bg-primary/[0.06]',
      dim && 'text-text-muted',
      className,
    )}
    data-next={next ? 'true' : 'false'}
    {...rest}
  >
    {glyph}
    <div className="min-w-0">{main}</div>
    {/* No state, no cell: an empty one would add a blank grid row (and its
        gap) on narrow containers. The action keeps its own column. */}
    {state != null && state !== false ? (
      <div className="col-start-2 flex min-w-0 flex-wrap items-center gap-1.5 @[720px]:col-start-3">{state}</div>
    ) : null}
    <div className="col-start-2 flex flex-wrap items-center justify-start gap-1.5 @[720px]:col-start-4 @[720px]:justify-end">{action}</div>
  </li>
);

export const StageRowName: React.FC<{ children: React.ReactNode; dim?: boolean }> = ({ children, dim }) => (
  <div className={cn('truncate text-[13px] font-semibold', dim ? 'text-text-secondary' : 'text-text-primary')}>{children}</div>
);

export const StageRowMeta: React.FC<{ children: React.ReactNode; 'data-testid'?: string; title?: string }> = ({ children, title, 'data-testid': testId }) => (
  <div className="truncate text-[11px] text-text-muted" title={title} data-testid={testId}>{children}</div>
);

/** The Stage 2 prerequisite, rendered as a step, never as an analysis. */
export const ScenarioStep: React.FC<{
  scenario: StageScenarioStep;
  screeningComplete: boolean;
  onAction?: (action: PathAction) => void;
  /** Labels of the analyses folded into this step — they run once the scenario is promoted. */
  then?: readonly string[];
  'data-testid'?: string;
}> = ({ scenario, screeningComplete, onAction, then = [], 'data-testid': testId = 'stage-scenario-step' }) => {
  if (!scenario.needed) return null;
  const live = screeningComplete;
  const title = scenario.stale ? 'Re-promote the generation scenario' : 'Promote a generation scenario';
  const subline = scenario.stale
    ? 'The promoted scenario changed · re-promote it in Generation & Impact'
    : live
      ? 'Solve and promote in Generation & Impact — Stage 2 runs against it'
      : 'After early screening · solve and promote in Generation & Impact';
  return (
    <div
      className="grid grid-cols-[22px_minmax(0,1fr)] items-center gap-x-2.5 gap-y-1.5 border-b border-dashed border-border bg-surface-2/60 px-3.5 py-2.5 sm:grid-cols-[22px_minmax(0,1fr)_auto] sm:gap-x-3"
      data-testid={testId}
      data-live={live ? 'true' : 'false'}
    >
      <StageGlyph kind={live ? 'priority' : 'later'} priority={1} />
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold text-text-primary">{title}</div>
        <div className="text-[11px] text-text-muted">{subline}</div>
        {then.length > 0 ? (
          <div className="text-[11px] text-text-muted" data-testid={`${testId}-then`}>
            Then · {then.join(' · ')}
          </div>
        ) : null}
      </div>
      {scenario.action && scenario.action.kind !== 'none' && onAction ? (
        <Button
          type="button"
          size="sm"
          variant={live ? 'mission-primary' : 'outline'}
          disabled={!live}
          onClick={() => onAction(scenario.action!)}
          className="col-start-2 w-fit sm:col-start-3"
          data-testid={`${testId}-action`}
          data-action-kind={scenario.action.kind}
        >
          {scenario.action.label}
          <ArrowRight aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
};

export const StageBoard: React.FC<{
  board: StageBoardModel;
  /** Lit when the next step lives on this board. */
  live?: boolean;
  /** Extra text under the count in the header (e.g. "available when inputs are ready"). */
  subline?: string;
  headerTestId?: string;
  children: React.ReactNode;
  foot?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}> = ({ board, live = false, subline, headerTestId, children, foot, className, 'data-testid': testId }) => {
  const chip = stageStateChip(board);
  return (
    <section
      className={cn('@container flex flex-col border bg-card', live ? 'border-primary' : 'border-border-subtle', className)}
      aria-label={board.label}
      data-testid={testId}
      data-state={board.state}
    >
      <header className="flex items-center justify-between gap-3 border-b border-border-subtle px-3.5 py-2.5" data-testid={headerTestId}>
        <div className="min-w-0">
          <div className="text-[12.5px] font-semibold text-text-primary">{board.label}</div>
          <div className="text-[11px] tabular-nums text-text-muted">
            {board.requiredTotal === 0
              ? 'nothing required'
              : `${board.requiredUsed} of ${board.requiredTotal} used${board.requiredCurrent < board.requiredUsed ? ` · ${board.requiredCurrent} current` : ''}`}
            {subline ? ` · ${subline}` : ''}
          </div>
        </div>
        {chip ? <StatusPill variant={chip.variant}>{chip.label}</StatusPill> : null}
      </header>
      {children}
      {foot ? (
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t border-border-subtle bg-surface-2/60 px-3.5 py-2 text-[11px] text-text-muted">
          {foot}
        </div>
      ) : null}
    </section>
  );
};

/** A compact "Current: ✓ A · ✓ B" foot for boards in queue mode. */
export const CurrentFoot: React.FC<{ labels: readonly string[] }> = ({ labels }) => (
  <>
    <span>Current:</span>
    {labels.map((label) => (
      <span key={label} className="inline-flex items-center gap-1 text-text-secondary">
        <CheckCircle2 className="h-3 w-3 text-success" aria-hidden />
        {label}
      </span>
    ))}
  </>
);

export default StageBoard;
